<script setup>
  import { toRef, computed, useTemplateRef } from 'vue'
  import { useI18n } from 'vue-i18n';
  import UpgradeButton from '@/components/UpgradeButton.vue';
  import Icon from '@/components/icons/Icon.vue';
  import { useContainerViewTracking } from '@/composables/useContainerViewTracking';
  import { useMembershipLevels } from '@/composables/useMembershipLevels';

  const props = defineProps({
    upgrade: {
      type: Array,
      required: true
    },
    productText: {
      type: [String, Object],
      required: true
    },
    buttonPosition: {
      type: String,
      default: 'inline'
    },
    clickLocation: {
      type: String,
      default: ''
    },
    trackingContainer: {
      type: String,
      required: false
    },
    // Prefixes the product with "all" in the message (e.g. "unlock all Pro Models")
    unlockAll: {
      type: Boolean,
      default: false
    }
  })

  const { t } = useI18n();
  const { formatMembershipLevels } = useMembershipLevels();

  const upgradeBlockElement = useTemplateRef('upgradeBlockElement');

  const productText = toRef(props.productText);

  const hasLevels = computed(() => Boolean(props.upgrade && props.upgrade[1] && props.upgrade[1].length));
  const messageKeypath = computed(() => {
    if (props.unlockAll) return hasLevels.value ? 'upgrade.unlockAllProductWithLevels' : 'upgrade.unlockAllProduct';

    return hasLevels.value ? 'upgrade.unlockProductWithLevels' : 'upgrade.unlockProduct';
  });
  const formattedLevels = computed(() => hasLevels.value ? formatMembershipLevels(props.upgrade[1]) : '');

  const buttonText = computed(() => {
    if (props.upgrade[2] === null) return t('upgrade.upgradeNow');

    return props.upgrade[2] ? t('upgrade.startFreeTrial') : t('upgrade.upgradeNow');
  });

  // Only the Complete Report opts into container view tracking
  if (props.trackingContainer) {
    useContainerViewTracking(upgradeBlockElement, () => ({ Container: props.trackingContainer }));
  }

</script>

<template>
  <div ref="upgradeBlockElement" class="jwx-upgrade-block-container">
    <div class="jwx-upgrade-block jwx-button-shadow" :data-button-position="props.buttonPosition">
      <div class="jwx-upgrade-block__content">
        <slot></slot>
        <i18n-t v-if="props.productText" :keypath="messageKeypath" tag="p" scope="global" class="jwx-upgrade-block__text">
          <template #levels><strong>{{ formattedLevels }}</strong></template>
          <template #product><strong>{{ productText }}</strong></template>
        </i18n-t>
      </div>
      <Icon name="lock-unlocked" class="jwx-upgrade-block__icon" />
      <UpgradeButton :buttonText="buttonText" :clickLocation="props.clickLocation" />
    </div>   
  </div>
</template>